import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { Option } from '@repo/toolkit/types'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import InnerBox from '@repo/ui/inner-box'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useMemo } from 'react'

import {
  getCountriesList,
  getCountriesListQueryKey
} from '../../../api/countries/get-countries-list'
import { Paths } from '../../../lib/constants/path'
import useTranslate from '../../../lib/hooks/use-translate'
import { ApplicationStatusEnum } from '../../../lib/types/enums/enrollment/application-status.enum'
import { getCountryLabel } from '../../../lib/types/models/country/country-list'
import { EnrollmentProperties } from '../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentLight } from '../../../lib/types/models/enrollment/enrollment-light'

export type EnrollmentDetailsMode = 'user' | 'trip'

type Props = {
  mode: EnrollmentDetailsMode
  enrollment: EnrollmentLight
}

function DetailSection({ label, value }: Option<string>) {
  return (
    <div className={'flex flex-col gap-1'}>
      <Typography variant={'label-semibold'} color={tailwindColors.grey.light.active}>
        {label}
      </Typography>
      <Typography variant={'label-semibold'}>{value}</Typography>
    </div>
  )
}

export function EnrollmentDetails({ mode, enrollment }: Props) {
  const { navigate } = useNavigator()
  const { childFirstName, childLastName } = enrollment
  const { currentLanguage } = useTranslate()

  const { data: countryList } = useQuery({
    queryKey: getCountriesListQueryKey(),
    queryFn: () => getCountriesList(),
    staleTime: Infinity
  })

  const dates = EnrollmentProperties.getEnrollmentDates(enrollment).value
  const journey = enrollment.mainProposition?.journey

  const tripUserDetails: Option<string>[] = useMemo(() => {
    if (mode === 'user')
      return [
        { label: 'Prénom', value: childFirstName ?? '' },
        { label: 'NOM', value: childLastName ?? '' }
      ]
    return []
  }, [mode])

  if (!dates || !countryList) return null

  return (
    <InnerBox className={'rounded-2xl flex flex-col gap-3 w-full'}>
      <Badge
        variant={enrollment.status === ApplicationStatusEnum.CONFIRMED ? 'success' : 'warning'}
        text={`Séjour ${journey?.reference ?? '-'}`}
      />

      <div className={'flex flex-col gap-3'}>
        {tripUserDetails.map((tud) => (
          <DetailSection key={tud.label} {...tud} />
        ))}

        <div className={'flex flex-col gap-1'}>
          <Typography variant={'label-semibold'} color={tailwindColors.grey.light.active}>
            Dates
          </Typography>
          <Badge
            variant={'grey'}
            text={`${dates.startDate} - ${dates.endDate}`}
            className={'whitespace-nowrap'}
          />
        </div>
        {[
          {
            label: 'Pays',
            value:
              getCountryLabel(
                currentLanguage,
                countryList?.data.find(
                  (c) => c.key === EnrollmentProperties.getEnrollmentCountry(enrollment).value
                )
              ) ?? ''
          },
          {
            label: 'Formule',
            value: EnrollmentProperties.getEnrollmentFormula(enrollment).value ?? ''
          },
          {
            label: 'Thème',
            value: EnrollmentProperties.getEnrollmentMainOption(enrollment).value ?? ''
          }
        ].map((td) => (
          <DetailSection key={td.label} {...td} />
        ))}
      </div>

      <Typography
        variant={'label-semibold'}
        color={tailwindColors.primary.DEFAULT}
        className={'cursor-pointer'}
        onClick={() =>
          navigate(Paths.ENROLLMENT_QUOTATION_UPDATE_PAGE.replace(':id', enrollment.id.toString()))
        }
      >
        Voir
      </Typography>
    </InnerBox>
  )
}
